import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';

export interface PalierPaiement {
  rang: number;
  delaiJours: number;
  ton: 'courtois' | 'ferme' | 'dernier_rappel';
  actif: boolean;
  objet?: { fr: string; en?: string };
  message?: { fr: string; en?: string };
}
export interface ReglagesPaiement {
  version: number;
  acceptePaiements: boolean;
  paiementPartiel: { autorise: boolean; minimumCentimes: number };
  dureeLienJours: number;
  relances: { mode: 'proposer' | 'automatique'; paliers: PalierPaiement[] };
}
export type CorpsReglagesPaiement = Omit<ReglagesPaiement, 'version'> & { versionAttendue: number };
export interface TentativePaiement {
  _id: string;
  montantCentimes: number;
  devise: string;
  etat: 'creation' | 'ouverte' | 'en_traitement' | 'payee' | 'echouee' | 'expiree' | 'abandonnee';
  codeEchec: string | null;
  createdAt: string;
  expireLe: string;
}
export interface EncaissementPaiement {
  _id: string;
  factureId: string;
  tentativeId: string;
  paiementId: string;
  devise: string;
  montantCentimes: number;
  imputeCentimes: number;
  excedentCentimes: number;
  fraisCentimes: number | null;
  reglementId: string | null;
  moyen: 'carte' | 'virement';
  payeLe: string;
  etat: 'impute' | 'a_regulariser' | 'regularise';
  rembourseExcedentCentimes: number;
  rembourseImputeCentimes: number;
  remboursements: {
    cle: string;
    part: 'excedent' | 'impute';
    montantCentimes: number;
    motif: string;
    etat: 'demande' | 'confirme' | 'echoue';
    avoirId: string | null;
    remboursementId: string | null;
    le: string;
  }[];
  alertes: {
    evenementId: string;
    type: 'remboursement_externe' | 'contestation';
    montantCentimes: number;
    inscrite: boolean;
    mode?: 'avoir' | 'traite';
    motif?: string;
  }[];
}
export interface RelancePaiement {
  _id: string;
  factureId: string;
  rang: number;
  ton: PalierPaiement['ton'];
  etat: 'a_envoyer' | 'en_cours' | 'a_verifier' | 'envoyee' | 'capture' | 'echec' | 'sans_objet';
  destinataire: string;
  resteDuCentimes: number;
  envoyeeLe: string | null;
  essais: number;
}
export type ResultatEnvoiPaiement = 'envoye' | 'capture' | 'echec';
export interface LienPaiement {
  id: string;
  etat: 'actif' | 'revoque' | 'echu' | 'solde';
  expireLe: string;
  revoqueLe: string | null;
  envois: { le: string; destinataire: string; resultat: ResultatEnvoiPaiement }[];
}
export interface FacturePaiement {
  factureId: string;
  resteDuCentimes: number;
  lienUrl: string | null;
  liens: LienPaiement[];
  tentatives: TentativePaiement[];
  encaissements: EncaissementPaiement[];
  relances: RelancePaiement[];
  suivi: { relancesSuspendues: boolean; motifSuspension: string } | null;
}
export interface TableauPaiement {
  impayes: {
    factureId: string;
    numero: string;
    echeanceLe: string | null;
    resteDuCentimes: number;
    rangPalier: number | null;
    relancesSuspendues: boolean;
  }[];
  impayesParPalier: { rang: number; nombre: number; resteDuCentimes: number }[];
  aRegulariser: number;
  relancesAEnvoyer: number;
  pagination: Pagination;
}
export interface CorpsRemboursementPaiement {
  part: 'excedent' | 'impute';
  montantCentimes: number;
  motif: string;
}
export interface CorpsAlertePaiement {
  mode: 'avoir' | 'traite';
  motif: string;
}
export interface FiltresPaiement {
  page?: number;
  parPage?: number;
}
export type FiltresEncaissements = FiltresPaiement & { etat?: EncaissementPaiement['etat'] };
export type FiltresRelances = FiltresPaiement & { etat?: RelancePaiement['etat'] };
const racine = (g: string) => `/g/${encodeURIComponent(g)}/paiement-en-ligne`;
const facture = (g: string, id: string) =>
  `/g/${encodeURIComponent(g)}/ventes/factures/${encodeURIComponent(id)}/paiement-en-ligne`;
function parametres(f: object) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined) p.set(k, String(v));
  return p.toString();
}
export const paiementEnLigneApi = {
  tableau: (g: string, f: FiltresPaiement = {}, a?: AppelApi) =>
    api.get<TableauPaiement>(`${racine(g)}?${parametres(f)}`, { galerie: g, ...a }),
  reglages: (g: string, a?: AppelApi) =>
    api.get<{ reglages: ReglagesPaiement }>(`${racine(g)}/reglages`, { galerie: g, ...a }),
  modifierReglages: (g: string, corps: CorpsReglagesPaiement, a?: AppelApi) =>
    api.put<{ reglages: ReglagesPaiement }>(`${racine(g)}/reglages`, corps, { galerie: g, ...a }),
  encaissements: (g: string, f: FiltresEncaissements = {}, a?: AppelApi) =>
    api.get<{ encaissements: EncaissementPaiement[]; pagination: Pagination }>(
      `${racine(g)}/encaissements?${parametres(f)}`,
      { galerie: g, ...a },
    ),
  encaissement: (g: string, id: string, a?: AppelApi) =>
    api.get<{ encaissement: EncaissementPaiement }>(
      `${racine(g)}/encaissements/${encodeURIComponent(id)}`,
      { galerie: g, ...a },
    ),
  rembourser: (g: string, id: string, corps: CorpsRemboursementPaiement, a?: AppelApi) =>
    api.post<{ encaissement: EncaissementPaiement }>(
      `${racine(g)}/encaissements/${encodeURIComponent(id)}/rembourser`,
      corps,
      { galerie: g, ...a },
    ),
  reprendre: (g: string, id: string, cle: string, a?: AppelApi) =>
    api.post<{ encaissement: EncaissementPaiement }>(
      `${racine(g)}/encaissements/${encodeURIComponent(id)}/remboursements/${encodeURIComponent(cle)}/reprendre`,
      {},
      { galerie: g, ...a },
    ),
  inscrireAlerte: (g: string, id: string, rang: number, corps: CorpsAlertePaiement, a?: AppelApi) =>
    api.post<{ encaissement: EncaissementPaiement }>(
      `${racine(g)}/encaissements/${encodeURIComponent(id)}/alertes/${rang}/inscrire`,
      corps,
      { galerie: g, ...a },
    ),
  relances: (g: string, f: FiltresRelances = {}, a?: AppelApi) =>
    api.get<{ relances: RelancePaiement[]; pagination: Pagination }>(
      `${racine(g)}/relances?${parametres(f)}`,
      { galerie: g, ...a },
    ),
  preparer: (g: string, a?: AppelApi) =>
    api.post<{ preparees: number }>(`${racine(g)}/relances/preparer`, {}, { galerie: g, ...a }),
  envoyerRelances: (g: string, ids: string[], a?: AppelApi) =>
    api.post<{ relances: (RelancePaiement | null)[] }>(
      `${racine(g)}/relances/envoyer`,
      { ids },
      { galerie: g, ...a },
    ),
  facture: (g: string, id: string, a?: AppelApi) =>
    api.get<FacturePaiement>(facture(g, id), { galerie: g, ...a }),
  creerLien: (g: string, id: string, a?: AppelApi) =>
    api.post<{ lien: LienPaiement & { url: string } }>(
      `${facture(g, id)}/lien`,
      {},
      { galerie: g, ...a },
    ),
  revoquerLien: (g: string, id: string, a?: AppelApi) =>
    api.delete<{ revoque: boolean }>(`${facture(g, id)}/lien`, { galerie: g, ...a }),
  envoyerLien: (g: string, id: string, a?: AppelApi) =>
    api.post<{ resultat: ResultatEnvoiPaiement }>(
      `${facture(g, id)}/envoyer`,
      {},
      { galerie: g, ...a },
    ),
  suspension: (
    g: string,
    id: string,
    corps: { suspendues: boolean; motif: string },
    a?: AppelApi,
  ) =>
    api.put<{ suivi: FacturePaiement['suivi'] }>(`${facture(g, id)}/suspension`, corps, {
      galerie: g,
      ...a,
    }),
};

export function useTableauPaiement(g: string, f: FiltresPaiement = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'paiement-en-ligne', 'tableau', f),
    queryFn: (c) => paiementEnLigneApi.tableau(g, f, appelDeRequete(c)),
  });
}

export function useReglagesPaiement(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'paiement-en-ligne', 'reglages'),
    queryFn: (c) => paiementEnLigneApi.reglages(g, appelDeRequete(c)),
  });
}

export function useEncaissementsPaiement(g: string, f: FiltresEncaissements = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'paiement-en-ligne', 'encaissements', f),
    queryFn: (c) => paiementEnLigneApi.encaissements(g, f, appelDeRequete(c)),
  });
}

export function useEncaissementPaiement(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'paiement-en-ligne', 'encaissement', id),
    queryFn: (c) => paiementEnLigneApi.encaissement(g, id, appelDeRequete(c)),
  });
}

export function useRelancesPaiement(g: string, f: FiltresRelances = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'paiement-en-ligne', 'relances', f),
    queryFn: (c) => paiementEnLigneApi.relances(g, f, appelDeRequete(c)),
  });
}

export function useFacturePaiement(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'paiement-en-ligne', 'facture', id),
    queryFn: (c) => paiementEnLigneApi.facture(g, id, appelDeRequete(c)),
  });
}

export function useModifierReglagesPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsReglagesPaiement, a) => paiementEnLigneApi.modifierReglages(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useRembourserPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; corps: CorpsRemboursementPaiement }, a) =>
        paiementEnLigneApi.rembourser(g, v.id, v.corps, a),
      onSettled: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useReprendreRemboursementPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; cle: string }, a) =>
        paiementEnLigneApi.reprendre(g, v.id, v.cle, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useInscrireAlertePaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; rang: number; corps: CorpsAlertePaiement }, a) =>
        paiementEnLigneApi.inscrireAlerte(g, v.id, v.rang, v.corps, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function usePreparerRelancesPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (_v: void, a) => paiementEnLigneApi.preparer(g, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useEnvoyerRelancesPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: string[], a) => paiementEnLigneApi.envoyerRelances(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useCreerLienPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: string, a) => paiementEnLigneApi.creerLien(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useRevoquerLienPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: string, a) => paiementEnLigneApi.revoquerLien(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useEnvoyerLienPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: string, a) => paiementEnLigneApi.envoyerLien(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}

export function useSuspensionPaiement(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; suspendues: boolean; motif: string }, a) =>
        paiementEnLigneApi.suspension(g, v.id, { suspendues: v.suspendues, motif: v.motif }, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'paiement-en-ligne') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'ventes') });
      },
    }),
  );
}
